'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Globe, Save, Loader2, AlertCircle, CheckCircle, ChevronDown, Check, Languages, Wand2, Lock, Info, ChevronRight, RefreshCw } from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import Image from 'next/image';
import { useTheme } from '@/hooks/useTheme';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';

interface LanguageSettingsProps {
  agentId: string;
  agentName: string;
  initialSettings: {
    defaultLanguage?: string;
    allowPersonalLanguagePreference?: boolean;
    widgetHeading?: string;
    welcomeMessage?: string;
    welcomeSubMessage?: string;
    buttonText?: string;
    placeholderQuestions?: string[];
    translations?: any;
  };
  isClientApi?: boolean;
  availableModels?: Array<{
    displayName: string;
    isEnabled: boolean;
    llmPlatformId: string;
    llmProvider: string;
    model: string;
    modelLabel?: string;
  }>;
}

const availableLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'jp', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
];

const getProviderLogo = (provider: string, isDark: boolean): string => {
  const logos: Record<string, { light: string; dark?: string }> = {
    OPENAI: {
      light: '/logos/openai.svg',
      dark: '/logos/openai-dm.svg',
    },
    ANTHROPIC: {
      light: '/logos/anthropic.webp',
    },
    GROQ: {
      light: '/logos/groq-square.png',
    },
    VECTORIZE: {
      light: '/logos/vectorize.svg',
    },
  };

  const providerLogos = logos[provider];
  if (!providerLogos) return '';

  return isDark && providerLogos.dark ? providerLogos.dark : providerLogos.light;
};

export default function LanguageSettings({ agentId, agentName, initialSettings, availableModels = [], isClientApi = false }: LanguageSettingsProps) {
  const { t, setLocale } = useTranslation();
  const { theme } = useTheme();
  const [defaultLanguage, setDefaultLanguage] = useState(initialSettings.defaultLanguage || 'en');
  const [allowPersonalLanguagePreference, setAllowPersonalLanguagePreference] = useState(initialSettings.allowPersonalLanguagePreference || false);
  
  // Initialize translations with configDoc values for default language if not present
  const initializeTranslations = () => {
    const initial = initialSettings.translations || {};
    
    // If default language translations don't exist, populate from configDoc
    if (!initial[defaultLanguage]) {
      if (isClientApi) {
        initial[defaultLanguage] = {
          name: agentName,
          widgetHeading: initialSettings.widgetHeading || '',
          welcomeMessage: initialSettings.welcomeMessage || '',
          buttonText: initialSettings.buttonText || '',
          placeholderQuestions: initialSettings.placeholderQuestions || []
        };
      } else {
        initial[defaultLanguage] = {
          name: agentName,
          welcomeMessage: initialSettings.welcomeMessage || '',
          welcomeSubMessage: initialSettings.welcomeSubMessage || '',
          placeholderQuestions: initialSettings.placeholderQuestions || []
        };
      }
    }
    
    return initial;
  };
  
  const [translations, setTranslations] = useState(initializeTranslations());
  const [hasTranslationChanges, setHasTranslationChanges] = useState(false);
  const [selectedTranslationLocale, setSelectedTranslationLocale] = useState(defaultLanguage);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showTranslationLocaleMenu, setShowTranslationLocaleMenu] = useState(false);
  const [selectedModel, setSelectedModel] = useState(availableModels?.[0]?.model || '');
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingPersonalPref, setIsSavingPersonalPref] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Use feature flags hook
  const { chatMultilingualEnabled, isLoading: loadingFlags } = useFeatureFlags(agentId);

  // Use agent hook for mutation and to get fresh data
  const { updateAgent, agent, configDoc } = useAgent(agentId);
  const organizationId = configDoc?.organizationId;
  
  // Get current values from general page
  const currentValues = useMemo(() => ({
    name: agent?.name || agentName,
    widgetHeading: configDoc.widgetHeading || '',
    welcomeMessage: configDoc.welcomeMessage || '',
    welcomeSubMessage: configDoc.welcomeSubMessage || '',
    buttonText: configDoc.buttonText || '',
    placeholderText: configDoc.placeholderText || '',
    placeholderQuestions: configDoc.placeholderQuestions || []
  }), [agent, agentName, configDoc]);

  // Check if any non-default locale has missing translations
  const hasAnyLocaleMissingTranslations = useMemo(() => {
    // Get all locales except the default
    const nonDefaultLocales = availableLanguages
      .filter(lang => lang.code !== defaultLanguage)
      .map(lang => lang.code);
    
    // Check each non-default locale
    for (const locale of nonDefaultLocales) {
      const localeTranslations = translations[locale];
      
      // If no translations at all for this locale, it's missing
      if (!localeTranslations) return true;
      
      // Check if all fields are empty
      const hasName = localeTranslations.name && localeTranslations.name.trim() !== '';
      const hasWidgetHeading = isClientApi && localeTranslations.widgetHeading && localeTranslations.widgetHeading.trim() !== '';
      const hasWelcome = localeTranslations.welcomeMessage && localeTranslations.welcomeMessage.trim() !== '';
      const hasSubMessage = !isClientApi && localeTranslations.welcomeSubMessage && localeTranslations.welcomeSubMessage.trim() !== '';
      const hasButtonText = isClientApi && localeTranslations.buttonText && localeTranslations.buttonText.trim() !== '';
      const hasQuestions = localeTranslations.placeholderQuestions && 
                          localeTranslations.placeholderQuestions.some((q: string) => q && q.trim() !== '');
      
      // If none of the fields have content, translations are missing
      if (!(hasName || hasWidgetHeading || hasWelcome || hasSubMessage || hasButtonText || hasQuestions)) {
        return true;
      }
    }
    
    return false;
  }, [translations, defaultLanguage, isClientApi]);

  // Check if default locale translations exist
  const defaultLocaleHasTranslations = useMemo(() => {
    const defaultTranslations = translations[defaultLanguage];
    if (!defaultTranslations) return false;
    
    // Check if at least one field has content
    const hasName = defaultTranslations.name && defaultTranslations.name.trim() !== '';
    const hasWidgetHeading = isClientApi && defaultTranslations.widgetHeading && defaultTranslations.widgetHeading.trim() !== '';
    const hasWelcome = defaultTranslations.welcomeMessage && defaultTranslations.welcomeMessage.trim() !== '';
    const hasSubMessage = !isClientApi && defaultTranslations.welcomeSubMessage && defaultTranslations.welcomeSubMessage.trim() !== '';
    const hasButtonText = isClientApi && defaultTranslations.buttonText && defaultTranslations.buttonText.trim() !== '';
    const hasQuestions = defaultTranslations.placeholderQuestions && 
                        defaultTranslations.placeholderQuestions.some((q: string) => q && q.trim() !== '');
    
    return hasName || hasWidgetHeading || hasWelcome || hasSubMessage || hasButtonText || hasQuestions;
  }, [translations, defaultLanguage, isClientApi]);

  // Check if default language translations match current values
  const defaultLocaleTranslationsMatch = useMemo(() => {
    const defaultTranslations = translations[defaultLanguage];
    if (!defaultTranslations) return true; // No translations means they match (will use general values)
    
    // Check if any field differs from current values
    if (defaultTranslations.name !== currentValues.name) return false;
    if (defaultTranslations.welcomeMessage !== currentValues.welcomeMessage) return false;
    if (isClientApi && defaultTranslations.widgetHeading !== currentValues.widgetHeading) return false;
    if (isClientApi && defaultTranslations.buttonText !== currentValues.buttonText) return false;
    if (!isClientApi && defaultTranslations.welcomeSubMessage !== currentValues.welcomeSubMessage) return false;
    
    // Check placeholder questions
    const translatedQuestions = defaultTranslations.placeholderQuestions || [];
    const configQuestions = currentValues.placeholderQuestions;
    
    if (translatedQuestions.length !== configQuestions.length) return false;
    
    for (let i = 0; i < translatedQuestions.length; i++) {
      if (translatedQuestions[i] !== configQuestions[i]) return false;
    }
    
    return true; // All match
  }, [translations, defaultLanguage, currentValues, isClientApi]);

  // Determine which warning to show
  const translationWarningType = useMemo(() => {
    if (!defaultLocaleTranslationsMatch) {
      // General page values don't match default locale translations
      return 'outdated';
    }
    
    if (hasAnyLocaleMissingTranslations) {
      // Some non-default locale is missing translations
      return 'missing-other-locales';
    }
    
    if (!defaultLocaleHasTranslations) {
      // No translations for default locale
      return 'missing-default';
    }
    
    return null; // No warning needed
  }, [defaultLocaleTranslationsMatch, hasAnyLocaleMissingTranslations, defaultLocaleHasTranslations]);

  // Auto-save function
  const autoSave = useCallback(
    async (settingsToSave: any) => {
      try {
        await updateAgent.mutateAsync(settingsToSave);
        showToast(t('admin.language.autoSaved'), 'success');
        
        // Invalidate cache to refresh agent data
        await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
        
        // If default language changed, update the UI locale and translation selector
        if (settingsToSave.defaultLanguage) {
          setSelectedTranslationLocale(settingsToSave.defaultLanguage);
          // Update the UI language immediately without reload
          setLocale(settingsToSave.defaultLanguage);
        }
      } catch (err) {
        showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
      }
    },
    [updateAgent, showToast, t, queryClient, agentId, setLocale]
  );

  // Auto-save default language
  useEffect(() => {
    if (defaultLanguage !== initialSettings.defaultLanguage) {
      const timer = setTimeout(() => {
        autoSave({ defaultLanguage });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [defaultLanguage, initialSettings.defaultLanguage, autoSave]);


  // Update translations when configDoc changes (e.g., after query invalidation)
  useEffect(() => {
    if (configDoc?.translations) {
      setTranslations((prevTranslations: any) => {
        // Merge new translations with existing ones
        const newTranslations = { ...prevTranslations, ...configDoc.translations };
        
        // Ensure default language has current values
        if (!newTranslations[defaultLanguage]) {
          newTranslations[defaultLanguage] = {
            name: agent?.name || agentName,
            welcomeMessage: configDoc.welcomeMessage || '',
            welcomeSubMessage: configDoc.welcomeSubMessage || '',
            placeholderQuestions: configDoc.placeholderQuestions || []
          };
        }
        
        return newTranslations;
      });
    }
  }, [configDoc?.translations, defaultLanguage, agent?.name, agentName, configDoc?.welcomeMessage, configDoc?.welcomeSubMessage, configDoc?.placeholderQuestions]);

  const handleSaveTranslations = async () => {
    setError('');
    setIsSaving(true);

    try {
      await updateAgent.mutateAsync({ translations });
      showToast(t('admin.language.translationsSaved'), 'success');
      setHasTranslationChanges(false); // Reset the flag after successful save
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateTranslations = async () => {
    if (!selectedModel) {
      setError(t('admin.language.translations.errors.noModelSelected'));
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const targetLangs = availableLanguages.filter((lang) => lang.code !== defaultLanguage).map((lang) => lang.code);

      const response = await fetch(`/api/chat/agents/${agentId}/generate-translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: selectedModel,
          sourceLang: defaultLanguage,
          targetLangs,
          content: currentValues,
        }),
      });

      if (!response.ok) {
        throw new Error(t('admin.language.translations.errors.generationFailed'));
      }

      const generatedTranslations = await response.json();
      
      // Update translations in state
      const updatedTranslations = {
        ...translations,
        ...generatedTranslations,
      };
      // Update default language translations to match current config values
      updatedTranslations[defaultLanguage] = {
        ...updatedTranslations[defaultLanguage],
        ...currentValues
      };
      setTranslations(updatedTranslations);
      
      // Automatically save the generated translations
      const saveResponse = await fetch(`/api/chat/agents/${agentId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          defaultLanguage,
          allowPersonalLanguagePreference,
          translations: updatedTranslations,
        }),
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save translations');
      }

      setHasTranslationChanges(false); // Reset changes flag after successful save
      
      // Show success toast
      showToast(
        `Successfully generated and saved translations for ${targetLangs.length} languages`,
        'success'
      );
      
      // Invalidate query to refresh data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.language.translations.errors.generationFailed'));
      showToast(
        err instanceof Error ? err.message : t('admin.language.translations.errors.generationFailed'),
        'error'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Show skeleton loader while loading feature flags
  if (loadingFlags) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Settings sections skeleton */}
          <div className="space-y-8">
            {/* Default Language skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                  <div className="h-3 w-80 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-3"></div>
                  <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Language Preferences skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-44 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-6"></div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                    <div className="h-3 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  </div>
                  <div className="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Translations skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-6"></div>
              <div className="space-y-6">
                <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                      <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                      <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button skeleton */}
          <div className="mt-8 flex justify-end">
            <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.language.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('admin.language.description')}</p>
        </div>

        {/* Settings sections */}
        <div className="space-y-8">
          {/* Default Language Settings */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.language.defaultLanguage.title')}</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('admin.language.defaultLanguage.label')}</label>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{availableLanguages.find((lang) => lang.code === defaultLanguage)?.flag}</span>
                      <span className="text-sm font-medium">{availableLanguages.find((lang) => lang.code === defaultLanguage)?.name}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Language Dropdown Menu */}
                  {showLanguageMenu && (
                    <div
                      className="absolute top-full left-0 right-0 mt-2 w-full bg-white dark:bg-gray-800 
                                  rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 
                                  py-2 z-50 max-h-80 overflow-y-auto"
                    >
                      {availableLanguages.map((lang) => {
                        const isSelected = lang.code === defaultLanguage;
                        return (
                          <button
                            key={lang.code}
                            type="button"
                            onClick={() => {
                              setDefaultLanguage(lang.code);
                              setShowLanguageMenu(false);
                            }}
                            className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 
                                     transition-colors text-left ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          >
                            <span className="text-2xl">{lang.flag}</span>
                            <span
                              className={`flex-1 text-sm ${
                                isSelected ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {lang.name}
                            </span>
                            {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('admin.language.defaultLanguage.help')}</p>
              </div>
            </div>
          </section>

          {/* Personal Language Preferences */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatMultilingualEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Globe className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.language.personalPreferences.title')}</h2>
              {!chatMultilingualEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>

            {!chatMultilingualEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.personalLanguage.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.personalLanguage.description')}</p>
                    <a
                      href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'}/organization/${organizationId}/admin/billing/changeplan`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                    >
                      {t('upgradeCTA.upgradeToPro')}
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <label htmlFor="allowPersonalLanguage" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.language.personalPreferences.allowLabel')}
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.language.personalPreferences.allowHelp')}</p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (chatMultilingualEnabled && !isSavingPersonalPref) {
                      const newValue = !allowPersonalLanguagePreference;
                      setAllowPersonalLanguagePreference(newValue);
                      setIsSavingPersonalPref(true);
                      
                      try {
                        await updateAgent.mutateAsync({ allowPersonalLanguagePreference: newValue });
                        showToast(t('admin.language.autoSaved'), 'success');
                      } catch (err) {
                        // Revert on error
                        setAllowPersonalLanguagePreference(!newValue);
                        showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
                      } finally {
                        setIsSavingPersonalPref(false);
                      }
                    }
                  }}
                  disabled={!chatMultilingualEnabled || isSavingPersonalPref}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${allowPersonalLanguagePreference ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                    ${!chatMultilingualEnabled || isSavingPersonalPref ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${allowPersonalLanguagePreference ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>

              {!allowPersonalLanguagePreference && chatMultilingualEnabled && (
                <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('admin.language.personalPreferences.disabledNote')}</p>
                </div>
              )}
            </div>
          </section>

          {/* Translations Section */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatMultilingualEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Languages className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.language.translations.title')}</h2>
              {!chatMultilingualEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 relative z-10">{t('admin.language.translations.description')}</p>

            {!chatMultilingualEnabled && (
              <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Languages className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.multilingual.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.multilingual.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'}/organization/${organizationId}/admin/billing/changeplan`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        {t('upgradeCTA.upgradeToPro')}
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('upgradeCTA.multilingual.features')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Locale Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.language.translations.selectLocale')}
              </label>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => chatMultilingualEnabled && setShowTranslationLocaleMenu(!showTranslationLocaleMenu)}
                  disabled={!chatMultilingualEnabled}
                  className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                           flex items-center justify-between transition-colors
                           ${chatMultilingualEnabled ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{availableLanguages.find((lang) => lang.code === selectedTranslationLocale)?.flag}</span>
                    <span className="text-sm font-medium">{availableLanguages.find((lang) => lang.code === selectedTranslationLocale)?.name}</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showTranslationLocaleMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Locale Dropdown Menu */}
                {showTranslationLocaleMenu && (
                  <div
                    className="absolute top-full left-0 right-0 mt-2 w-full bg-white dark:bg-gray-800 
                                rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 
                                py-2 z-50 max-h-80 overflow-y-auto"
                  >
                    {availableLanguages.map((lang) => {
                      const isSelected = lang.code === selectedTranslationLocale;
                      return (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => {
                            setSelectedTranslationLocale(lang.code);
                            setShowTranslationLocaleMenu(false);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 
                                   transition-colors text-left ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        >
                          <span className="text-2xl">{lang.flag}</span>
                          <span
                            className={`flex-1 text-sm ${
                              isSelected ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {lang.name}
                          </span>
                          {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Translation Warnings */}
            {chatMultilingualEnabled && translationWarningType && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <RefreshCw className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    {translationWarningType === 'outdated' && (
                      <>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">
                          {t('admin.language.translations.outdatedWarning.title')}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {t('admin.language.translations.outdatedWarning.description')}
                        </p>
                      </>
                    )}
                    {translationWarningType === 'missing-other-locales' && (
                      <>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">
                          {t('admin.language.translations.missingOtherLocalesWarning.title')}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {t('admin.language.translations.missingOtherLocalesWarning.description')}
                        </p>
                      </>
                    )}
                    {translationWarningType === 'missing-default' && (
                      <>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">
                          {t('admin.language.translations.missingDefaultWarning.title')}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {t('admin.language.translations.missingDefaultWarning.description')}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Translation Fields */}
            <div className="space-y-4 mb-6">
              {/* Widget Heading - Only for WIDGET agents */}
              {isClientApi && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Widget Heading
                    </label>
                    {selectedTranslationLocale === defaultLanguage && 
                     defaultLocaleHasTranslations &&
                     translations[selectedTranslationLocale]?.widgetHeading !== currentValues.widgetHeading && 
                     chatMultilingualEnabled && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                        {t('admin.language.translations.outdated')}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={(() => {
                      // For default language, always use configDoc value
                      if (selectedTranslationLocale === defaultLanguage) {
                        return currentValues.widgetHeading;
                      }
                      // For other languages, use translation if available
                      if (translations[selectedTranslationLocale]?.widgetHeading) {
                        return translations[selectedTranslationLocale].widgetHeading;
                      }
                      // For other locales with no translations, show empty
                      return '';
                    })()}
                    onChange={(e) => {
                      if (chatMultilingualEnabled && selectedTranslationLocale !== defaultLanguage) {
                        setTranslations({
                          ...translations,
                          [selectedTranslationLocale]: {
                            ...translations[selectedTranslationLocale],
                            widgetHeading: e.target.value,
                          },
                        });
                        setHasTranslationChanges(true);
                      }
                    }}
                    disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             ${(!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={currentValues.widgetHeading || agentName || 'Chat Assistant'}
                  />
                </div>
              )}
              
              {/* Agent Name */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.language.translations.fields.agentName')}
                  </label>
                  {selectedTranslationLocale === defaultLanguage && 
                   defaultLocaleHasTranslations &&
                   translations[selectedTranslationLocale]?.name !== currentValues.name && 
                   chatMultilingualEnabled && (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                      {t('admin.language.translations.outdated')}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={(() => {
                    // For default language, always use configDoc value
                    if (selectedTranslationLocale === defaultLanguage) {
                      return currentValues.name;
                    }
                    // For other languages, use translation if available
                    if (translations[selectedTranslationLocale]?.name) {
                      return translations[selectedTranslationLocale].name;
                    }
                    // For other locales with no translations, show empty
                    return '';
                  })()}
                  onChange={(e) => {
                    if (chatMultilingualEnabled && selectedTranslationLocale !== defaultLanguage) {
                      setTranslations({
                        ...translations,
                        [selectedTranslationLocale]: {
                          ...translations[selectedTranslationLocale],
                          name: e.target.value,
                        },
                      });
                      setHasTranslationChanges(true);
                    }
                  }}
                  disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                           ${(!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  placeholder={currentValues.name}
                />
              </div>

              {/* Welcome Message */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.language.translations.fields.welcomeMessage')}
                  </label>
                  {selectedTranslationLocale === defaultLanguage && 
                   defaultLocaleHasTranslations &&
                   translations[selectedTranslationLocale]?.welcomeMessage !== currentValues.welcomeMessage && 
                   chatMultilingualEnabled && (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                      {t('admin.language.translations.outdated')}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={(() => {
                    // For default language, always use configDoc value
                    if (selectedTranslationLocale === defaultLanguage) {
                      return currentValues.welcomeMessage;
                    }
                    // For other languages, use translation if available
                    if (translations[selectedTranslationLocale]?.welcomeMessage) {
                      return translations[selectedTranslationLocale].welcomeMessage;
                    }
                    return '';
                  })()}
                  onChange={(e) => {
                    if (chatMultilingualEnabled && selectedTranslationLocale !== defaultLanguage) {
                      setTranslations({
                        ...translations,
                        [selectedTranslationLocale]: {
                          ...translations[selectedTranslationLocale],
                          welcomeMessage: e.target.value,
                        },
                      });
                      setHasTranslationChanges(true);
                    }
                  }}
                  disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                           ${(!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  placeholder={currentValues.welcomeMessage || 'Welcome to the chat'}
                />
              </div>

              {/* Welcome Sub-message or Button Text */}
              {isClientApi ? (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('admin.language.translations.fields.buttonText')}
                    </label>
                    {selectedTranslationLocale === defaultLanguage && 
                     defaultLocaleHasTranslations &&
                     translations[selectedTranslationLocale]?.buttonText !== currentValues.buttonText && 
                     chatMultilingualEnabled && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                        {t('admin.language.translations.outdated')}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={(() => {
                      // For default language, always use configDoc value
                      if (selectedTranslationLocale === defaultLanguage) {
                        return currentValues.buttonText;
                      }
                      // For other languages, use translation if available
                      if (translations[selectedTranslationLocale]?.buttonText) {
                        return translations[selectedTranslationLocale].buttonText;
                      }
                      return '';
                    })()}
                    onChange={(e) => {
                      if (chatMultilingualEnabled && selectedTranslationLocale !== defaultLanguage) {
                        setTranslations({
                          ...translations,
                          [selectedTranslationLocale]: {
                            ...translations[selectedTranslationLocale],
                            buttonText: e.target.value,
                          },
                        });
                        setHasTranslationChanges(true);
                      }
                    }}
                    disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             ${(!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={currentValues.buttonText || 'Chat with us'}
                  />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('admin.language.translations.fields.welcomeSubMessage')}
                    </label>
                    {selectedTranslationLocale === defaultLanguage && 
                     defaultLocaleHasTranslations &&
                     translations[selectedTranslationLocale]?.welcomeSubMessage !== currentValues.welcomeSubMessage && 
                     chatMultilingualEnabled && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                        {t('admin.language.translations.outdated')}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={(() => {
                      // For default language, always use configDoc value
                      if (selectedTranslationLocale === defaultLanguage) {
                        return currentValues.welcomeSubMessage;
                      }
                      // For other languages, use translation if available
                      if (translations[selectedTranslationLocale]?.welcomeSubMessage) {
                        return translations[selectedTranslationLocale].welcomeSubMessage;
                      }
                      return '';
                    })()}
                    onChange={(e) => {
                      if (chatMultilingualEnabled && selectedTranslationLocale !== defaultLanguage) {
                        setTranslations({
                          ...translations,
                          [selectedTranslationLocale]: {
                            ...translations[selectedTranslationLocale],
                            welcomeSubMessage: e.target.value,
                          },
                        });
                        setHasTranslationChanges(true);
                      }
                    }}
                    disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             ${!chatMultilingualEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={currentValues.welcomeSubMessage || 'How can I help you today?'}
                  />
                </div>
              )}

              {/* Input Placeholder - Only for WIDGET agents */}
              {isClientApi && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Input Placeholder
                    </label>
                    {selectedTranslationLocale === defaultLanguage && 
                     defaultLocaleHasTranslations &&
                     translations[selectedTranslationLocale]?.placeholderText !== currentValues.placeholderText && 
                     chatMultilingualEnabled && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                        {t('admin.language.translations.outdated')}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={(() => {
                      // For default language, always use configDoc value
                      if (selectedTranslationLocale === defaultLanguage) {
                        return currentValues.placeholderText;
                      }
                      // For other languages, use translation if available
                      if (translations[selectedTranslationLocale]?.placeholderText) {
                        return translations[selectedTranslationLocale].placeholderText;
                      }
                      return '';
                    })()}
                    onChange={(e) => {
                      if (selectedTranslationLocale !== defaultLanguage) {
                        setTranslations({
                          ...translations,
                          [selectedTranslationLocale]: {
                            ...translations[selectedTranslationLocale],
                            placeholderText: e.target.value,
                          },
                        });
                        setHasTranslationChanges(true);
                      }
                    }}
                    disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             ${(!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={currentValues.placeholderText || 'Type your message...'}
                  />
                </div>
              )}

              {/* Placeholder Questions */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.language.translations.fields.placeholderQuestions')}
                  </label>
                  {selectedTranslationLocale === defaultLanguage && defaultLocaleHasTranslations && chatMultilingualEnabled && (() => {
                    const translatedQuestions = translations[selectedTranslationLocale]?.placeholderQuestions || [];
                    const configQuestions = currentValues.placeholderQuestions;
                    const hasOutdated = translatedQuestions.some((q: string, i: number) => q !== (configQuestions[i] || ''));
                    return hasOutdated ? (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                        {t('admin.language.translations.outdated')}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="space-y-2">
                  {(isClientApi ? [0, 1, 2] : [0, 1, 2, 3]).map((index) => {
                    const getQuestionValue = () => {
                      // For default language, always use configDoc value
                      if (selectedTranslationLocale === defaultLanguage) {
                        return currentValues.placeholderQuestions[index] || '';
                      }
                      // For other languages, use translation if available
                      const translatedQuestion = translations[selectedTranslationLocale]?.placeholderQuestions?.[index];
                      if (translatedQuestion) {
                        return translatedQuestion;
                      }
                      return '';
                    };
                    
                    return (
                      <input
                        key={index}
                        type="text"
                        value={getQuestionValue()}
                        onChange={(e) => {
                          if (!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) return;
                          const defaultQuestions = isClientApi ? ['', '', ''] : ['', '', '', ''];
                          const newQuestions = [...(translations[selectedTranslationLocale]?.placeholderQuestions || defaultQuestions)];
                          newQuestions[index] = e.target.value;
                          setTranslations({
                            ...translations,
                            [selectedTranslationLocale]: {
                              ...translations[selectedTranslationLocale],
                              placeholderQuestions: newQuestions,
                            },
                          });
                          setHasTranslationChanges(true);
                        }}
                        disabled={!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                 ${(!chatMultilingualEnabled || selectedTranslationLocale === defaultLanguage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        placeholder={currentValues.placeholderQuestions[index] || t('common.questionPlaceholder', { number: index + 1 })}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Generate Translations Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">{t('admin.language.translations.generateTitle')}</h3>

              {/* Model Selection - Full Width */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.language.translations.selectModel')}
                </label>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => chatMultilingualEnabled && setShowModelMenu(!showModelMenu)}
                    disabled={!chatMultilingualEnabled || availableModels.length === 0}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             flex items-center justify-between 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                             ${chatMultilingualEnabled && availableModels.length > 0 ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : ''}"
                  >
                    <div className="flex items-center gap-3">
                      {(() => {
                        const selectedModelData = availableModels.find((m) => m.model === selectedModel);
                        const logo = selectedModelData ? getProviderLogo(selectedModelData.llmProvider, theme === 'dark') : '';
                        return logo ? (
                          <Image src={logo} alt={selectedModelData?.llmProvider || ''} width={20} height={20} className="rounded" />
                        ) : null;
                      })()}
                      <span className="text-sm">
                        {availableModels.find((m) => m.model === selectedModel)?.modelLabel ||
                          availableModels.find((m) => m.model === selectedModel)?.model ||
                          t('admin.language.translations.selectModelPlaceholder')}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showModelMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Model Dropdown Menu */}
                  {showModelMenu && (
                    <div
                      className="absolute top-full left-0 right-0 mt-2 w-full bg-white dark:bg-gray-800 
                                  rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 
                                  py-2 z-50 max-h-80 overflow-y-auto"
                    >
                      {availableModels.map((model) => {
                        const isSelected = model.model === selectedModel;
                        return (
                          <button
                            key={model.model}
                            type="button"
                            onClick={() => {
                              setSelectedModel(model.model);
                              setShowModelMenu(false);
                            }}
                            className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 
                                     transition-colors text-left ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          >
                            {(() => {
                              const logo = getProviderLogo(model.llmProvider, theme === 'dark');
                              return logo ? (
                                <Image src={logo} alt={model.llmProvider} width={20} height={20} className="rounded flex-shrink-0" />
                              ) : (
                                <div className="w-5 h-5 flex-shrink-0" />
                              );
                            })()}
                            <div className="flex-1">
                              <p
                                className={`text-sm ${
                                  isSelected ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {model.modelLabel || model.model}
                              </p>
                              {model.displayName && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{model.displayName}</p>}
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons - Aligned Right */}
              <div className="flex justify-end gap-3">
                {/* Generate Button (Secondary - Green) */}
                <button
                  onClick={handleGenerateTranslations}
                  disabled={!chatMultilingualEnabled || isGenerating || !selectedModel}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('admin.language.translations.generating')}
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-4 w-4" />
                      {t('admin.language.translations.generateButton')}
                    </>
                  )}
                </button>

                {/* Save Button (Primary) */}
                <button
                  onClick={handleSaveTranslations}
                  disabled={!chatMultilingualEnabled || isSaving || !hasTranslationChanges}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('admin.language.savingTranslations')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t('admin.language.saveTranslations')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Error Messages (for translations section only) */}
        {error && (
          <div className="mt-6 flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
